'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import Pagination from '@/components/shared/Pagination';
import SearchBar from '@/components/shared/SearchBar';
import { useScrollIntoViewOnChange } from '@/hooks/useScrollIntoViewOnChange';
import { useUrlSearch } from '@/hooks/useUrlSearch';
import { cn } from '@/utils/cn';
import { withSearchQuery } from '@/utils/paginationHref.utils';
import { GameListContent } from './components/GameList';
import {
  FILTERED_GAME_LIST_DESKTOP_PAGE_SIZE,
  filteredGameListQueryOptions,
} from './filteredGameList.queries';

type FilterByCategory = { categoryId: string; providerId?: never };
type FilterByProvider = { providerId: string; categoryId?: never };
type FilteredGameListFilter = FilterByCategory | FilterByProvider;
type FilteredGameListProps = {
  className?: string;
  gridClassName?: string;
  page?: number;
  pageHrefBase?: string;
} & FilteredGameListFilter;

export const FilteredGameList = (props: FilteredGameListProps) => {
  const { className, gridClassName, page, pageHrefBase } = props;
  const isShowProviderList = 'providerId' in props;
  const pageSize = FILTERED_GAME_LIST_DESKTOP_PAGE_SIZE;

  const t = useTranslations('providers.game-list');

  const { searchValue, setSearchValue, searchParam, pageParam, setPageParam } = useUrlSearch({
    searchKey: 'q',
    resetPageOnSearch: !pageHrefBase,
  });
  const activePage = page ?? pageParam;
  const sectionRef = useScrollIntoViewOnChange(activePage);
  const providerId = props.providerId;

  const { data: gamesData, isFetching, isSuccess } = useQuery(
    providerId
      ? filteredGameListQueryOptions({
          page: activePage,
          search: searchParam,
          limit: pageSize,
          providerId,
        })
      : filteredGameListQueryOptions({
          page: activePage,
          search: searchParam,
          limit: pageSize,
          categoryId: props.categoryId!,
        }),
  );

  const totalPages = gamesData?.totalPages || pageSize;
  const totalItems = gamesData?.totalItems || 0;
  const getPageHref = (nextPage: number) => {
    if (!pageHrefBase) {
      return `?page=${nextPage}`;
    }

    const href = nextPage <= 1 ? pageHrefBase : `${pageHrefBase}/${nextPage}`;

    return withSearchQuery(href, searchParam);
  };

  return (
    <section
      ref={sectionRef}
      className={cn('scroll-m-25', className)}
    >
      <div className={cn('relative w-full', isShowProviderList && 'mb-10 md:mb-6')}>
        <SearchBar
          query={searchValue}
          clearSearch={() => setSearchValue('')}
          onChange={e => setSearchValue(e.target.value)}
          placeholder={t('search-placeholder')}
        />
        <GameListContent
          isLoading={isFetching || !isSuccess}
          items={gamesData?.items}
          defaultPageSize={pageSize}
          className={gridClassName}
        />
      </div>

      {totalItems > pageSize && (
        <Pagination
          variant={pageHrefBase ? 'link' : 'button'}
          className={isShowProviderList ? undefined : 'mt-10'}
          totalPages={totalPages}
          totalItems={totalItems}
          pageNumber={gamesData?.page || 1}
          onPageChangeAction={pageHrefBase ? undefined : setPageParam}
          getHrefAction={getPageHref}
        />
      )}
    </section>
  );
};
